import React from 'react';
import { Box, LinearProgress } from '@mui/material';
import { Library, Sparkles, Info, Subtitles } from 'lucide-mui';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { ToggleSetting, SettingsSection } from './common';
import { usePlan } from '../plan/hooks/plan';

const Usage: React.FC = () => {
  const { plan, library } = useSelector((state: RootState) => state);
  const { usage, packageProperties } = plan;
  const { isPlan } = usePlan();

  const usageLimits = [
    {
      key: 'transcriptionCount',
      label: 'Transcriptions',
      tooltip: 'Number of transcriptions used this month',
      value: usage.transcriptionCount || 0,
      max: packageProperties.transcriptionCount || 0,
      icon: <Subtitles />,
    },
    {
      key: 'librarySize',
      label: 'Library Size',
      tooltip: 'Number of files in your library',
      value: library.files.data.length || 0,
      max: packageProperties.librarySize || 0,
      icon: <Library />,
    },
    {
      key: 'askAICount',
      label: 'AI Credits',
      tooltip: 'Number of AI credits used this month',
      value: usage.askAICount || 0,
      max: packageProperties.askAICount || 0,
      icon: <Sparkles />,
    },
  ];

  const isBusinessPlan = isPlan(['BUSINESS']);
  const isFreePlan = isPlan(['FREE']);

  const getDescription = (limit: typeof usageLimits[0]) => {
    const percentage = Math.round((limit.value / limit.max) * 100);
    return `${limit.value} of ${limit.max} used (${percentage}%)`;
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      {isBusinessPlan ? (
        <SettingsSection title="Business Plan Usage">
          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label={usageLimits[2].label}
            description={getDescription(usageLimits[2])}
            icon={usageLimits[2].icon}
          >
            <Box sx={{ width: '100%', mt: -1 }}>
              <LinearProgress
                variant="determinate"
                value={(usageLimits[2].value / usageLimits[2].max) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </ToggleSetting>
        </SettingsSection>
      ) : (
        <SettingsSection title="Usage Limits">
          {isFreePlan && (
            <ToggleSetting
              checked={false}
              onChange={() => {}}
              label="Free Plan"
              description="You are currently on the free plan. Upgrade to access more features and higher limits."
              icon={<Info />}
            />
          )}
          {usageLimits.map((limit) => (
            <ToggleSetting
              key={limit.key}
              checked={false}
              onChange={() => {}}
              label={limit.label}
              description={getDescription(limit)}
              icon={limit.icon}
            >
              <Box sx={{ width: '100%', mt: -1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(limit.value / limit.max) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            </ToggleSetting>
          ))}
        </SettingsSection>
      )}
    </Box>
  );
};

export default Usage;
